/* eslint-disable max-lines */
import { useMemo } from 'react';
import {
  ConsultationType,
  PageSection,
  PageType,
  SubSection,
  TrackingData,
} from '../../../common/AnalyticsContext/TrackingData';
import { useAnalytics } from '../../../common/AnalyticsContext/useAnalytics';
import { STATE_PER_METRO } from '../../../common/components/SearchHeader/constants';
import { FilterChoice } from '../../../common/components/SearchHeader/types/FilterChoice';
import { FacetResult, FreeTextSearch } from '../../../common/schema/hannibal/types.generated';
import { PAGE_SIZE } from '../../constants';
import { SearchResultProfile } from '../../types';
import {
  renderDateFilter,
  analyticsGender,
  renderTimeFilter,
  getlocationChoiceTrackingType,
} from './helpers';
import { profilesToAppearanceTrackingIds } from './profilesToAppearanceTrackingIds';

interface SearchPageAnalyticsProps {
  filters: FilterChoice;
  extendedResults?: FreeTextSearch;
  total?: number;
  profiles: SearchResultProfile[];
  searchSpecialty?: string;
  typesFacet?: readonly FacetResult[] | null;
}

export const useSearchPageAnalytics = ({
  filters,
  typesFacet,
  profiles,
  total,
  searchSpecialty,
  extendedResults,
}: SearchPageAnalyticsProps) =>
  useAnalytics(
    useMemo(
      () =>
        getSearchPageAnalytics({
          profiles,
          filters,
          searchSpecialty,
          typesFacet,
          total,
          extendedResults,
        }),
      [filters, profiles, searchSpecialty, total, typesFacet, extendedResults],
    ),
  );

export const getSearchPageAnalytics = ({
  profiles,
  filters,
  searchSpecialty,
  typesFacet,
  total = 0,
  extendedResults,
}: SearchPageAnalyticsProps): TrackingData => {
  const totalPracticesDisplayed = profiles.filter(profiles => profiles.type === 'practice').length;

  const totalPractitionersDisplayed = profiles.filter(profile => profile.type === 'practitioner')
    .length;

  const lastPageOfProfiles = profiles.slice(PAGE_SIZE * -1);
  const lastPageOfPractices = lastPageOfProfiles.filter(it => it.type === 'practice');
  const lastPageOfPracticeIds = lastPageOfPractices.map(it => it.id);
  const lastPageOfPractitioners = lastPageOfProfiles.filter(it => it.type === 'practitioner');
  const lastPageOfPractitionerIds = lastPageOfPractitioners.map(it => it.id);
  const totalProfilesShown = profiles.length;
  const totalProfilesShownOfLastPage = lastPageOfProfiles.length;
  const appointmentsReturned = totalProfilesShownOfLastPage;

  const featuredListingIds = lastPageOfProfiles
    .filter(it => it.type === 'practice' && it.featured)
    .map(it => Number(it.id));

  const specialties = (() => {
    const set = new Set<string>();

    lastPageOfProfiles.forEach(item => {
      item.specialties.forEach(it => set.add(it));
    });

    return [...set];
  })();

  const suburb = filters.location?.suburb;

  const countProfileWithinSuburb = suburb
    ? profiles.filter(it => it.type === 'practice' && it.address?.suburb === suburb?.name).length
    : undefined;

  const filterCount = [
    /** Any of these should count as an 'applied' filter
     * if they are truthy
     */
    filters.gender,
    filters.location,
    filters.onlineAppointments,
    filters.language,
    filters.topic?.bulkBillingGp,
    filters.topic?.specialist,
    filters.topic?.treatment,
    filters.flag,
    filters.paymentMethod,
    filters.pricingAvailable,
    filters.governmentScheme,
    filters.acceptsNewPatients,
    filters.insurance,
    filters.specialOffer,
    filters.hicaps,
  ].filter(Boolean).length;

  const state =
    filters.location?.state ??
    suburb?.state ??
    (filters.location?.metro && STATE_PER_METRO[filters.location.metro]);

  const specialty = filters.topic?.bulkBillingGp
    ? 'General Practice'
    : filters.topic?.specialist && searchSpecialty;

  const practiceAppearanceTracking = profilesToAppearanceTrackingIds(lastPageOfPractices);

  const practitionerAppearanceTracking = profilesToAppearanceTrackingIds(lastPageOfPractitioners);

  const analytics: TrackingData = {
    pageData: {
      pageType: PageType.SEARCH_RESULTS,
      section: PageSection.CONSUMER,
      isSPA: true,
      subsection: filters.onlineAppointments
        ? SubSection.APPOINTMENT_SEARCH
        : SubSection.DIRECTORY_SEARCH,
    },
    searchData: {
      term: filters.text,
      state: state,
      postcode: suburb?.postcode || undefined,
      suburb: suburb?.name || undefined,
      filtersApplied: {
        bulkBill: filters.topic?.bulkBillingGp,
        consultationType:
          filters.flag === 'telehealth' ? ConsultationType.TELEHEALTH : ConsultationType.ALL,
        count: filterCount,
        date: renderDateFilter(filters),
        gender: analyticsGender(filters.gender),
        govScheme: filters.governmentScheme,
        onlineAppts: filters.onlineAppointments ? true : undefined,
        paymentMethod: filters.paymentMethod,
        practitionerLanguage: filters.language,
        pricingAvailable: filters.pricingAvailable,
        acceptsNewPatients: filters.acceptsNewPatients,
        specialty: specialty,
        time: renderTimeFilter(filters),
        specialOffers: filters.specialOffer,
        preferredInsProvider: filters.insurance ?? undefined,
        hicaps: filters.paymentMethod === 'HICAPS',
        location: {
          type: getlocationChoiceTrackingType(filters.location),
          suburb: suburb?.name || undefined,
          postcode: suburb?.postcode || undefined,
          state: filters.location?.state,
          region: filters.location?.metro,
        },
        vaccineType: filters.vaccineType,
      },
    },
    /** To be overridden by individual results if needed */
    resultsData: {
      extendedDisplayed: extendedResults?.searchProfiles?.results?.length ?? 0,
      extendedReturned: extendedResults?.searchProfiles.totalCount ?? 0,
      appearanceTracking: {
        practices: practiceAppearanceTracking,
        practitioners: practitionerAppearanceTracking,
      },
      hasInterests: false,
      hasPreferredProvider: false,
      limitedAppointmentsMsgShown: false,
      practicesReturned: typesFacet?.find(it => it.term === 'practice')?.count ?? 0,
      practitionersReturned: typesFacet?.find(it => it.term === 'practitioner')?.count ?? 0,
      appointmentsReturned,
      practiceList: lastPageOfPracticeIds,
      practitionerList: lastPageOfPractitionerIds,
      practicesDisplayed: totalPracticesDisplayed,
      practitionersDisplayed: totalPractitionersDisplayed,
      // These are rounded up to 1 since zero results are still considered a 'single page' by our analytics
      paginationAvailable: Math.ceil(total / PAGE_SIZE) || 1,
      paginationLevel: Math.ceil(totalProfilesShown / PAGE_SIZE) || 1,
      resultsDisplayed: totalProfilesShown,
      totalDisplayed: totalProfilesShown + (extendedResults?.searchProfiles?.results?.length ?? 0),
      resultsReturned: total,
      totalReturned: total + (extendedResults?.searchProfiles.totalCount ?? 0),
      featuredPracticeList: featuredListingIds.length !== 0 ? featuredListingIds : undefined,
      featuredResultsDisplayed: featuredListingIds.length,
      specialty: filters.topic?.specialist && searchSpecialty ? [searchSpecialty] : specialties,
      hasPricing: false,
      resultsInSuburbDisplayed: countProfileWithinSuburb,
    },
  };

  return analytics;
};
